import { logOperational } from 'analytics/snowplow-events/logging'
import request, { authOptions } from 'api/requestUtils'
import config from 'constants/config'
import debounce from 'lodash/debounce'
import qs from 'qs'
import { FlexRecommendation, Recommendation, mapFlexRecommendation, offerRecommendationMap } from './mappers/recommendationMap'

function getLereEnvParameters(): Record<string, string> {
  return config.LERE_USE_MOCKS ? { use_mocks: 'true' } : {}
}

export function getLereApiHostOverride(): string | undefined {
  return config.LERE_OVERRIDE_API_HOST || undefined
}

interface ApiError {
  status: number;
  errors: string;
}

function isApiError(obj: any): obj is ApiError {
  return Number.isFinite(obj.status) && obj.errors
}

function logError(apiTag: string, error?: any, extraParams: Record<string, any> = {}) {
  // __ is just a convenient separator - it is unlikely error messages will have double __ in them
  const label = (() => {
    if (error instanceof Error) {
      return `${error.name}__${error.message}`
    }
    if (isApiError(error)) {
      return `${error.status}__${error.errors}`
    }
    return error
  })()

  logOperational(apiTag, `${label}__${JSON.stringify(extraParams)}__errored`)
}

interface RecommendationURLParams {
  checkIn?: string;
  checkOut?: string;
  availableMonth?: string;
  duration?: number;
  region?: string;
}

class RecommendationsURLBuilder {
  // @ts-ignore
  private baseUrl: string
  private offerId: string
  private queryParams: Record<string, string | number> = {}

  constructor(offerId: string) {
    this.offerId = offerId
  }

  withChallengerCalendarBaseUrl() {
    this.baseUrl = `/api/challenger-recommendation/offers/${this.offerId}/calendar`
    return this
  }

  withCalendarBaseUrl() {
    this.baseUrl = `/api/recommendation/offers/${this.offerId}/calendar`
    return this
  }

  withChallengerBaseUrl() {
    this.baseUrl = `/api/challenger-recommendation/offers/${this.offerId}`
    return this
  }

  withBaseUrl() {
    this.baseUrl = `/api/recommendation/offers/${this.offerId}`
    return this
  }

  withChallengerCacheBaseUrl() {
    this.baseUrl = `/api/challenger-recommendation/offers/${this.offerId}/cache`
    return this
  }

  withCacheBaseUrl() {
    this.baseUrl = `/api/recommendation/offers/${this.offerId}/cache`
    return this
  }

  withQueryParams(queryParams: RecommendationURLParams) {
    this.queryParams = { ...this.queryParams, ...queryParams }
    return this
  }

  withLereEnvParameters() {
    this.queryParams = { ...this.queryParams, ...getLereEnvParameters() }
    return this
  }

  withRoomParams(rooms?: App.Occupants[]) {
    if (rooms?.length) {
      const [room] = rooms
      if (Number.isFinite(room.adults)) {
        this.queryParams.adults = `${room.adults}`
      }
      if (room.childrenAge?.length) {
        // @ts-ignore: won't be here when rooms is undefined
        this.queryParams.childrenAge = rooms[0].childrenAge.join(',')
      }
    }
    return this
  }

  build() {
    return `${this.baseUrl}?${qs.stringify(this.queryParams)}`
  }
}

export interface Filters {
  checkIn?: string;
  availableMonth?: string;
  rooms?: App.Occupants[];
  duration?: number;
}

interface LereResult {
  recommendations: App.Recommendation[];
  lereVersion: string | undefined;
}

export async function getRecommendations(offerId: string, region: string, enableChallenger: boolean, checkIn?: string, checkOut?: string): Promise<LereResult> {
  try {
    const builder = new RecommendationsURLBuilder(offerId)
      .withLereEnvParameters()
      .withQueryParams({
        region,
        checkIn,
        checkOut,
      })
    const url = enableChallenger ? builder.withChallengerBaseUrl().build() : builder.withBaseUrl().build()
    const response = await request.get<App.ApiResponse<Recommendation[]>>(
      url,
      { credentials: 'include' },
      getLereApiHostOverride(),
    )
    const { lere_version } = response.meta || {}

    const recommendations = response.result.map(recommendation => offerRecommendationMap(recommendation))
    return { recommendations, lereVersion: lere_version }
  } catch (error) {
    logError('getRecommendations', error, { offerId, region, enableChallenger, checkIn, checkOut })
    throw error
  }
}

export function getChildrenAgesValid(rooms: App.Occupants[]) {
  return (rooms || []).every((room) => {
    return (room.childrenAge || []).every((age) => age >= 0)
  })
}

export async function getCalendarRecommendations(
  offerId: string,
  region: string,
  filters: Filters,
  enableChallenger: boolean,
): Promise<LereResult> {
  try {
    const { checkIn, rooms, availableMonth, duration } = filters

    if (!getChildrenAgesValid(rooms)) {
      throw new Error('Some children ages are < 0')
    }

    const useCalendarAPI = !checkIn
    const builder = new RecommendationsURLBuilder(offerId)
      .withLereEnvParameters()
      .withQueryParams({
        checkIn,
        duration,
        region,
      })
      .withRoomParams(rooms)

    if (useCalendarAPI) {
      if (enableChallenger) {
        builder.withChallengerCalendarBaseUrl().withQueryParams({ availableMonth })
      } else {
        builder.withCalendarBaseUrl().withQueryParams({ availableMonth })
      }
    } else {
      if (enableChallenger) {
        builder.withChallengerBaseUrl()
      } else {
        builder.withBaseUrl()
      }
    }

    const url = builder.build()
    const response = await request.get<App.ApiResponse<Recommendation[]>>(
      url,
      { credentials: 'include' },
      getLereApiHostOverride(),
    )
    const { lere_version } = response.meta || {}
    const recommendations = response.result.map(recommendation => offerRecommendationMap(recommendation))
    return { recommendations, lereVersion: lere_version }
  } catch (error) {
    logError('getCalendarRecommendations', error, { offerId, region, filters, enableChallenger })
    throw error
  }
}

export async function cacheRecommendations(offerId: string, region: string, filters: Pick<Filters, 'duration' | 'rooms'>, enableChallenger: boolean) {
  try {
    const { duration, rooms } = filters
    if (!getChildrenAgesValid(rooms)) {
      throw new Error('Some children ages are < 0')
    }
    const builder = new RecommendationsURLBuilder(offerId)
      .withLereEnvParameters()
      .withQueryParams({
        duration,
        region,
      })
      .withRoomParams(rooms)
    const url = enableChallenger ? builder.withChallengerCacheBaseUrl().build() : builder.withCacheBaseUrl().build()
    await request.get<App.ApiResponse<Recommendation[]>>(
      url,
      { credentials: 'include' },
      getLereApiHostOverride(),
    )
  } catch (error) {
    logError('cacheRecommendations', error, { offerId, filters, region, enableChallenger })
    throw error
  }
}

export type PolygonMode = 'polygon_restricted' | 'polygon_excluded' | 'all_included'

interface LocationSeededParams {
  destinationId: string,
  polygonMode: PolygonMode,
  nearDestination: boolean,
  challengerEnabled: boolean,
  checkInDate?: string,
  checkOutDate?: string,
  duration?: number,
  flexibleDaysOnCheckIn?: number,
  useCentroid?: boolean,
}

export function getLocationSeededRecommendations({
  destinationId,
  polygonMode,
  nearDestination,
  challengerEnabled,
  checkInDate,
  checkOutDate,
  duration,
  flexibleDaysOnCheckIn,
  useCentroid,
}: LocationSeededParams): Promise<LereResult> {
  const params = {
    ...getLereEnvParameters(),
    destinationId,
    polygon_mode: polygonMode,
    nearDestination,
    check_in: checkInDate,
    check_out: checkOutDate,
    duration,
    flex: flexibleDaysOnCheckIn,
    use_centroid: useCentroid,
  }

  const baseUrl = challengerEnabled ? '/api/challenger-recommendation/locations' : '/api/recommendation/locations'
  const url = [baseUrl, qs.stringify(params)].join('?')

  return request.get<App.ApiResponse<Recommendation[]>>(
    url,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).then((response) => {
    const { lere_version } = response.meta || {}

    const recommendations = response.result.map(recommendation => offerRecommendationMap(recommendation))
    return { recommendations, lereVersion: lere_version }
  })
}

export interface GetLocationFlexRecommendations {
  regionCode: string;
  destinationId: string;
  checkInDate: string;
  duration: number;
  flexibleDaysOnCheckIn: number;
  numberOfAdults: number;
  childrenAges: number[];
}

interface LereFlexResult {
  recommendations: App.FlexRecommendation[];
  lereVersion: string | undefined;
}

export function getLocationFlexRecommendations({
  regionCode,
  destinationId,
  checkInDate,
  duration,
  numberOfAdults,
  childrenAges,
  flexibleDaysOnCheckIn,
}: GetLocationFlexRecommendations): Promise<LereFlexResult> {
  const params = {
    ...getLereEnvParameters(),
    region: regionCode,
    destination_id: destinationId,
    check_in: checkInDate,
    duration,
    adult: numberOfAdults,
    children_age: childrenAges.join(','),
    flex: flexibleDaysOnCheckIn,
    polygon_mode: 'polygon_restricted',
  }

  const baseUrl = '/api/recommendation/locations'
  const url = [baseUrl, qs.stringify(params)].join('?')

  return request.get<App.ApiResponse<FlexRecommendation[]>>(
    url,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).then((response) => {
    const { lere_version } = response.meta || {}

    const recommendations = response.result.map(recommendation => mapFlexRecommendation(recommendation, duration))
    return { recommendations, lereVersion: lere_version }
  })
}

interface ApiAbandonedCartItem {
  availability: number;
  offer_id: string;
  offer_type: string;
  offer_booking_type: string;
  package_id: string;
  room_rate_id: string;
  travel_start: string;
  travel_end: string;
  duration: number;
  number_of_adults: number;
  property_fees: number;
  extra_guest_surcharge: number;
  surcharge: number;
  price: number;
  total: number;
  children_ages: number[];
}
interface ApiPersonalisedAbandonedCart {
    transaction_key: string
    derived_tstamp: string
    subscriber_key: string
    user_id: string
    region: string
    currency: string
    total: number
    rank_no?: number | null
    cart_items: Array<ApiAbandonedCartItem>
}

function mapToCarts(result: ApiPersonalisedAbandonedCart[]): App.PersonalisedAbandonedCarts[] {
  return result.map((apiCart) => ({
    transactionKey: apiCart.transaction_key,
    derivedTstamp: apiCart.derived_tstamp,
    region: apiCart.region,
    currency: apiCart.currency,
    total: apiCart.total,
    rankNo: apiCart.rank_no,
    cartItems: (apiCart.cart_items || []).map((item) => ({
      offerId: item.offer_id,
      offerType: item.offer_type,
      offerBookingType: item.offer_booking_type,
      packageId: item.package_id,
      roomRateId: item.room_rate_id,
      travelStart: item.travel_start,
      travelEnd: item.travel_end,
      duration: item.duration,
      numberOfAdults: item.number_of_adults,
      propertyFees: item.property_fees,
      extraGuestSurcharge: item.extra_guest_surcharge,
      surcharge: item.surcharge,
      price: item.price,
      total: item.total,
      childrenAges: item.children_ages,
      availability: item.availability,
    })),
  }))
}

function clientVariantToSVCVariant(variant: App.LerePersonalisedAbandonedCartsVariant) {
  switch (variant) {
    case 'naive-model':
      return '0'
    case 'optimised-model':
      return '1'
    default:
      return '0'
  }
}

export async function getPersonalisedAbandonedCarts(userId: string, variant: App.LerePersonalisedAbandonedCartsVariant, region: string) {
  const acVersion = clientVariantToSVCVariant(variant)

  const url = [
    `/api/recommendation/personalisation/${userId}`,
    qs.stringify({ ac_version: acVersion, categories: 'abandoned_cart', region, request_lpp: !!config.LERE_ABANDONED_CART_REQUEST_LPP }),
  ].join('?')

  const response = await request.get<App.ApiResponse<{ abandoned_cart: ApiPersonalisedAbandonedCart[] }>>(
    url,
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  const carts = mapToCarts(response.result.abandoned_cart)
  const { lere_version } = response.meta || {}
  return { carts, lereVersion: lere_version }
}

export function putDismissPersonalisedAbandonedCarts(userId: string, variant: App.LerePersonalisedAbandonedCartsVariant, offerId: string) {
  const acVersion = clientVariantToSVCVariant(variant)

  const url = [
    `/api/recommendation/personalisation/dismiss/${userId}`,
    qs.stringify({ ac_version: acVersion, categories: 'abandoned_cart', offer_id: offerId }),
  ].join('?')

  return request.put(
    url,
    undefined,
    { credentials: 'include' },
    getLereApiHostOverride(),
  )
}

/**
 * Fetch the cart state from LERE backend
 * Note that the returned cart could either be the latest saved state,
 * OR older AC based on a recommendation algorithm
 */
export async function fetchAbandonedCart(userId: string, region: string) {
  const url = [
    `/api/recommendation/personalisation/${userId}/abandoned_cart`,
    qs.stringify({ region }),
  ].join('?')

  const response = await request.get<App.ApiResponse<App.CheckoutCartState>>(
    url,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).catch(console.error)
  return response ? response.result : undefined
}

/**
 * Save the cart state in LERE backend
 *
 * Note: we need to save this in LERE instead of the cart service because
 * the GET API has more complexity than simply returing what we saved
 */
export async function saveAbandonedCart(userId: string, state: App.CheckoutCartState) {
  const url = `/api/recommendation/personalisation/${userId}/abandoned_cart`

  return await request.put(
    url,
    state,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).catch(console.error)
}

export const saveCartInLereServiceWithDebounce = debounce(saveAbandonedCart, 500)

// result.context.audience_params.offer_id
interface ContextResult {
  context: {
    audience_params?: {
      offer_ids: string[];
    }
  };
}

export async function getPersonalisedContextViewHistory(userId: string) {
  const url = [
    `/api/recommendation/personalisation/${userId}`,
    qs.stringify({ categories: 'context' }),
  ].join('?')

  const response = await request.get<App.ApiResponse<ContextResult>>(
    url,
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  const offerIds = response.result.context?.audience_params?.offer_ids || []
  const { lere_version } = response.meta || {}
  return { offerIds, lereVersion: lere_version }
}

export async function getPeopleLikeMeRecommendations(userId: string, region: string) {
  const url = [`/api/recommendation/personalisation/${userId}/people_like_me`, qs.stringify({ region })].join('?')
  const response = await request.get<App.ApiResponse<string[]>>(
    url,
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  const { lere_version } = response.meta || {}
  return { offerIds: response.result, lereVersion: lere_version }
}

export async function getPersonalisationExistsForUser(userId: string, region: string) {
  if (config.ALWAYS_ENABLE_TOP_PICS) {
    return { exists: true, lereVersion: 'mock-lere-personalisation-for-user-exists' }
  }
  const url = [`/api/recommendation/personalisation/${userId}/people_like_me/check_user`, qs.stringify({ region })].join('?')
  const response = await request.get<App.ApiResponse<boolean>>(
    url,
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  const { lere_version } = response.meta || {}
  return { exists: response.result, lereVersion: lere_version }
}

export async function getTopDeals(region: string) {
  const response = await request.get<App.ApiResponse<{ offer_id: string, offer_type: string }[]>>(
    ['/api/recommendation/top_deals', qs.stringify({ region })].join('?'),
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  const { lere_version } = response.meta || {}
  return { offers: response.result, lereVersion: lere_version }
}

export async function getHeroPlanner(region: string) {
  const response = await request.get<App.ApiResponse<string[]>>(
    ['/api/recommendation/hero_planner', qs.stringify({ region })].join('?'),
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  const { lere_version } = response.meta || {}
  return { offerIds: response.result, lereVersion: lere_version }
}

export async function getHotLead(userId: string) {
  const response = await request.get<App.ApiResponse<App.OfferHotLead>>(
    `/api/recommendation/personalisation/${userId}/hot_leads`,
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  const { lere_version } = response.meta || {}
  return { hotLeadData: response.result, lereVersion: lere_version }
}

export async function saveHotLead(userId: string, hotLeadData: App.OfferHotLead) {
  await request.put(
    `/api/recommendation/personalisation/${userId}/hot_leads`,
    hotLeadData,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).catch(console.error)
}

interface LereRecentlyViewedOffer {
  offer_id: string;
  view_time: number;
}

export async function getRecentlyViewedOffers(userId: string, accessToken?: string) {
  const response = await request.get<App.ApiResponse<Array<LereRecentlyViewedOffer>>>(
    `/api/recommendation/personalisation/${userId}/recently_viewed`,
    authOptions(accessToken),
    getLereApiHostOverride(),
  )

  const { lere_version: lereVersion } = response.meta ?? {}
  const lereOffers = response.result ?? []
  const offers: App.RecentlyViewedOffer[] = lereOffers.map(offer => ({
    offerId: offer.offer_id,
    viewTime: offer.view_time,
  }))
  return { offers, lereVersion }
}

export async function saveRecentlyViewedOffers(userId: string, offers: Array<App.RecentlyViewedOffer>) {
  if (!offers.length) { // the add API only accept non-empty list
    return
  }

  const lereOffers: LereRecentlyViewedOffer[] = offers.map(offer => ({
    offer_id: offer.offerId,
    view_time: offer.viewTime,
  }))

  await request.post(
    // we need to post an array and have to disable the brand pre-processor
    // else it will turn the request body into an object
    `/api/recommendation/personalisation/${userId}/recently_viewed?without_brand=1`,
    lereOffers,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).catch(console.error)
}

export async function removeRecentlyViewedOffers(userId: string, offerIds: string[]) {
  await request.post(
    // we need to post an array and have to disable the brand pre-processor
    // else it will turn the request body into an object
    `/api/recommendation/personalisation/${userId}/recently_viewed/remove?without_brand=1`,
    offerIds,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).catch(console.error)
}
